import React from "react";
import {css} from "@emotion/css";
import moment from "moment";
import {themeTokens} from "@octopusdeploy/design-system-tokens";
import repository from "client/repository";
import {
    DataTable,
    DataTableBody,
    DataTableRow,
    DataTableRowColumn,
    DataTableRowHeaderColumn
} from "components/DataTable";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import PaperLayout from "components/PaperLayout";
import {Refresh} from "components/DataBaseComponent/DataBaseComponent";
import {BackgroundTaskResource} from "client/resources/backgroundTaskResource";
import {BackgroundTaskStatus} from "client/resources/backgroundTaskStatus";
import {displayTaskStatus} from "areas/backgroundTasks/displayTaskStatus";
import {BackgroundProcessResource} from "client/resources/backgroundProcessResource";
import {Step} from "client/resources/step";
import ActionButton from "components/Button";
import {getProcessDescription} from "areas/backgroundTasks/getProcessDescription";
import {shortProcessName} from "shared/shortProcessName";
import {FriendlyLocalTime} from "shared/FriendlyLocalTime";
import DurationBetweenLabel from "components/TimeLabels/DurationBetweenLabel";
import {RouteComponentProps} from "react-router-dom";
import {getStepDescription} from "areas/backgroundTasks/getStepDescription";
import splitStringIntoWords from "utils/StringHelper/splitStringIntoWords";
import ExternalLink from "components/Navigation/ExternalLink";
import externalSystemLinks from "externalSystemLinks";
import OverflowMenu from "components/Menu/OverflowMenu";
import routeLinks from "routeLinks";
import { Callout, List, ListItem, ListItemContent } from "@octopusdeploy/design-system-components";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import ActionList from "components/ActionList";
import {fromPairs, head, uniqBy} from "lodash";
import MoveToStepConfirmationDialogButton from "./MoveToStepConfirmationDialogButton";
import {EnvironmentName} from "client/resources/environmentName";
import {InstanceTaskLockResource} from "../../client/resources/instanceTaskLockResource";
import {ManageSubscriptionDialog} from "./ManageSubscriptionDialog";
import DiagnosticFilesTable from "areas/instances/Instances/DiagnosticFilesTable";
import { FileResource } from "~/client/resources/fileResource";
import CloudPortalFilesTable from "~/areas/files/CloudPortalFilesTable";

const humanizer = require("humanize-duration").humanizer({
    language: "shortEn",
    languages: { shortEn: { d: () => "d", h: () => "h", m: () => "m", s: () => "s", }, },
    units: ["h", "m"],
    largest: 2,
    round: true,
    spacer: "",
    delimiter: " "
});

// Bulk tasks (e.g. bulk reprovision) can have thousands of related tasks; fetching each one individually doesn't scale
const maxRelatedTasksToDescribe = 20;
const finishedStatuses = [BackgroundTaskStatus.Success, BackgroundTaskStatus.Failed, BackgroundTaskStatus.Cancelled];

const nextStepStyle = css`
    font-weight: bold;
    color: ${themeTokens.color.text.warning};
`;

const currentStepStyle = css`
    font-weight: bold;
    color: ${themeTokens.color.text.success};
`;

const stepsListStyle = css`
    .MuiListItem-root + .MuiListItem-root {
        border-top: none !important;
    }
    .MuiListItem-root > div {
        padding: 0.5rem;
    }
`;

const sleepTimeStyle = css`
    color: ${themeTokens.color.text.tertiary};
`;

interface State extends DataBaseComponentState {
    task?: BackgroundTaskResource;
    processes: BackgroundProcessResource[];
    environment?: EnvironmentName;
    instanceLock?: InstanceTaskLockResource;
    subscribed?: boolean;
    diagnosticFiles?: FileResource[];
    diagnosticFilesLoaded?: boolean;
    cloudPortalFiles?: FileResource[];
    cloudPortalFilesLoaded?: boolean;
    // null means the related task couldn't be loaded, e.g. it has been deleted
    relatedTasks: Record<string, BackgroundTaskResource | null>;
}

type Props = RouteComponentProps<{ backgroundTaskId: string }>;

export class BackgroundTaskDetails extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            processes: [],
            relatedTasks: {}
        };
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            const appInfo = await repository.Configuration.getAppInfo();
            const subscription = await repository.BackgroundTasks.getSubscription(this.props.match.params.backgroundTaskId);
            this.setState({environment: appInfo?.Environment, subscribed: !!subscription});
            this.doRefresh = await this.startRefreshLoop(() => this.loadData(), 5000);
        });
    }

    async loadData() {

        const [task, processes] = await Promise.all([
            repository.BackgroundTasks.get(this.props.match.params.backgroundTaskId),
            repository.BackgroundTasks.listProcesses()
        ]);

        let lock = null;
        if (task.HostedInstanceIdToLock) {
            const paginatedLocks = await repository.HostedInstances.getInstanceTaskLocks({relatedToHostedInstanceIds: [task.HostedInstanceIdToLock]});
            lock = head(paginatedLocks.Resources);
        }

        let diagnosticFiles = null;
        let diagnosticFilesLoaded = this.state.diagnosticFilesLoaded;
        if (diagnosticFilesLoaded)
        {
            diagnosticFiles = this.state.diagnosticFiles;
        }
        else if (task.DiagnosticFiles.length > 0 && task.Status === BackgroundTaskStatus.Success) {
            diagnosticFiles = await repository.HostedInstances.getDiagnosticFiles(task.State.HostedInstanceId, task.DiagnosticFiles);
            diagnosticFilesLoaded = true;
        }

        let cloudPortalFiles = null;
        let cloudPortalFilesLoaded = this.state.cloudPortalFilesLoaded;
        if (cloudPortalFilesLoaded)
        {
            cloudPortalFiles = this.state.cloudPortalFiles;
        }
        else if (task.CloudPortalFiles.length > 0 && task.Status === BackgroundTaskStatus.Success) {
            cloudPortalFiles = await repository.Files.getCloudPortalFiles(task.CloudPortalFiles);
            cloudPortalFilesLoaded = true;
        }

        const relatedTasks = await this.loadRelatedTasks(task);

        return {
            task,
            processes,
            relatedTasks,
            instanceLock: lock,
            diagnosticFiles,
            diagnosticFilesLoaded,
            cloudPortalFiles,
            cloudPortalFilesLoaded,
        };
    }

    render() {

        return <PaperLayout busy={this.state.busy}
                            errors={this.state.errors}
                            title={this.props.match.params.backgroundTaskId}
                            sectionControl={<ActionList actions={this.state.task ? this.getActions(this.state.task) : []} />}>
            {this.state.task && this.state.processes && this.getBody()}
        </PaperLayout>;
    }

    private getBody() {
        const task = this.state.task;
        const dequeueAfter = task.DequeueAfter ? `(for ${humanizer(moment(task.DequeueAfter).utc().diff(new Date()))})` : null;

        return <div>
            {this.getLockWarning(this.state.instanceLock, this.state.task)}
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Id</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{task.Id}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {displayTaskStatus(task.Status)}&nbsp;
                            <span className={sleepTimeStyle}>{dequeueAfter}</span>
                            {task.ErrorMessage && <Callout title="Most recent error" type="danger">
                                <pre>
                                    {task.ErrorMessage}
                                </pre>
                            </Callout>}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Process</DataTableRowHeaderColumn>
                        <DataTableRowColumn><span title={getProcessDescription(task.ProcessId, this.state.processes)}>{shortProcessName(task.ProcessId)}</span></DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Next Step Id</DataTableRowHeaderColumn>
                        <DataTableRowColumn><span title={getStepDescription(task.ProcessId, task.NextStepId, this.state.processes)}>{splitStringIntoWords(task.NextStepId)}</span>&nbsp;</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Current Step Id</DataTableRowHeaderColumn>
                        <DataTableRowColumn><span title={getStepDescription(task.ProcessId, task.CurrentStepId, this.state.processes)}>{splitStringIntoWords(task.CurrentStepId)}</span>&nbsp;</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Created</DataTableRowHeaderColumn>
                        <DataTableRowColumn><FriendlyLocalTime time={task.Created}/></DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Started</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{task.Started && <FriendlyLocalTime time={task.Started}/>}&nbsp;</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Duration</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{task.Started && task.Started && <DurationBetweenLabel from={task.Started} to={task.Finished || moment().toISOString()}/>}&nbsp;</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>State</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            <pre>
                                {JSON.stringify(task.State, null, 4)}
                            </pre>
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Related Documents</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            <List<{id: string; url: string}>
                                items={this.findLinksToDocumentsIn(task.State)}
                                rowKey={(item) => item.id}
                                renderRow={({item}) => (
                                    <ListItem>
                                        <ListItemContent>
                                            {this.describeRelatedDocument(item)}
                                        </ListItemContent>
                                    </ListItem>
                                )}
                                accessibleName="Related documents"
                            />
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Steps</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{this.getProcessDialog()}</DataTableRowColumn>
                    </DataTableRow>

                    { this.state.diagnosticFiles && <DataTableRow>
                        <DataTableRowHeaderColumn>Instance Files</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            <DiagnosticFilesTable instanceId={task.State.HostedInstanceId} files={this.state.diagnosticFiles} doBusyTask={this.doBusyTask} onFilesChanged={this.diagnosticFilesChanged} />
                        </DataTableRowColumn>
                    </DataTableRow> }

                    { this.state.cloudPortalFiles && <DataTableRow>
                        <DataTableRowHeaderColumn>Cloud Portal Files</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            <CloudPortalFilesTable files={this.state.cloudPortalFiles} doBusyTask={this.doBusyTask} onFilesChanged={this.cloudPortalFilesChanged} />
                        </DataTableRowColumn>
                    </DataTableRow> }

                </DataTableBody>
            </DataTable>
        </div>;
    }

    private tryToGetHubDeploymentTaskFor(step: Step, task: BackgroundTaskResource) {
        return  task.HubDeploymentTasks[step.Id];
    }

    private getTaskStepText(step: Step, task: BackgroundTaskResource) {
        const hubDeploymentTask = this.tryToGetHubDeploymentTaskFor(step, task)
        const hubDeploymentTaskLink = !!hubDeploymentTask ? <> — <ExternalLink href={hubDeploymentTask.WebUrl}>Hub Task</ExternalLink></> : null;
        return <span className={this.getCurrentStepStyling(step, task)}>{splitStringIntoWords(step.Id)} <ExternalLink href={externalSystemLinks.seq.backgroundTaskStep(task, step)} />{hubDeploymentTaskLink}</span>;
    }

    private getActions(task: BackgroundTaskResource) {
        const { environment } = this.state;

        const menuItems = [
            task.NextStepId  && OverflowMenu.item("Process Now", () => this.processNow(task)),
            !task.Finished && OverflowMenu.item("Cancel", () => this.cancel(task)),
            OverflowMenu.externalNavItem("Logs (Seq)", externalSystemLinks.seq.backgroundTask(task)),
            environment && OverflowMenu.externalNavItem("Logs (Sumo)", externalSystemLinks.sumoLogic.backgroundTaskLogs(task, environment)),
            task.TraceId && OverflowMenu.externalNavItem("Trace (Honeycomb)", externalSystemLinks.honeycomb.backgroundTaskTrace(task)),
            OverflowMenu.navItem("Audit Trail", routeLinks.audit.auditTrailFor(task)),
            OverflowMenu.dialogItem("Manage subscription", this.manageSubscriptionDialog()),
        ];

        return [
            <ActionButton label={this.state.subscribed ? "Unsubscribe" : "Subscribe"} onClick={this.toggleSubscription}/>,
            <ActionButton label="Refresh" onClick={this.doRefresh}/>,
            <OverflowMenu menuItems={menuItems}/>
        ];
    }

    private getCurrentStateText(step: Step, task: BackgroundTaskResource) {
        switch (step.Id) {
            case task.NextStepId: return <div className={this.getCurrentStepStyling(step, task)}>Next</div>;
            case task.CurrentStepId: return <div className={this.getCurrentStepStyling(step, task)}>Current</div>;
            default: return <div/>;
        }
    }

    private getCurrentStepStyling(step: Step, task: BackgroundTaskResource) {
        switch (step.Id) {
            case task.NextStepId: return nextStepStyle;
            case task.CurrentStepId: return currentStepStyle;
            default: return "";
        }
    }

    private getAction(step: Step, task: BackgroundTaskResource) {
        if (task.Status === BackgroundTaskStatus.Success) {
            return <div/>;
        }

        if (step.Id === task.CurrentStepId || step.Id === task.NextStepId){
            const hubDeploymentTask = this.tryToGetHubDeploymentTaskFor(step, task);
            if (hubDeploymentTask && hubDeploymentTask.WebUrl && task.Status === BackgroundTaskStatus.Cancelled)
            {
                return <div>
                    <OverflowMenu
                        menuItems={[OverflowMenu.item("Cancel Hub Task", () => this.cancelHubTask(task, step))]}/>
                </div>;
            } else {
                return <div/>;
            }
        }

        return <div>
            <MoveToStepConfirmationDialogButton
                stepDescription = {splitStringIntoWords(step.Id)}
                onConfirm={() => this.moveTo(step, task)}/>
        </div>;
    }

    private cancelHubTask(task: BackgroundTaskResource, step: Step) {
        return this.doBusyTask(async () => {
            await repository.BackgroundTasks.cancelHubTask(task, step);
            await this.doRefresh();
            return true;
        });
    }

    private moveTo(step: Step, task: BackgroundTaskResource) {
        return this.doBusyTask(async () => {
            await repository.BackgroundTasks.nextStep(task, step);
            await this.doRefresh();
            return true;
        });
    }

    private processNow(task: BackgroundTaskResource) {
        return this.doBusyTask(async () => {
            await repository.BackgroundTasks.resume(task);
            await this.doRefresh();
            return true;
        });
    }

    private cancel(task: BackgroundTaskResource) {
        return this.doBusyTask(async () => {
            await repository.BackgroundTasks.cancel(task);
            await this.doRefresh();
            return true;
        });
    }

    private async loadRelatedTasks(task: BackgroundTaskResource) {
        const cached = this.state.relatedTasks;
        const relatedTaskIds = this.findLinksToDocumentsIn(task.State)
            .map(link => link.id)
            .filter(id => id.toLocaleLowerCase().startsWith("backgroundtasks-"));
        if (relatedTaskIds.length > maxRelatedTasksToDescribe) {
            return cached;
        }

        const taskIdsToFetch = relatedTaskIds
            .filter(id => !(id in cached) || (cached[id] && !finishedStatuses.includes(cached[id].Status)));

        const fetched = await Promise.all(taskIdsToFetch.map(id =>
            repository.BackgroundTasks.get(id).then(t => [id, t], () => [id, null])));

        return {...cached, ...fromPairs(fetched)};
    }

    private describeRelatedDocument(document: {id: string; url: string}) {
        const relatedTask = this.state.relatedTasks[document.id];
        if (!relatedTask) {
            return <InternalLink to={document.url}>{document.id}</InternalLink>;
        }

        return <span>
            <InternalLink to={document.url}>
                <span title={relatedTask.Intent}>{shortProcessName(relatedTask.ProcessId)}</span>
            </InternalLink>
            {" · "}{displayTaskStatus(relatedTask.Status)}
            {" · created "}<FriendlyLocalTime time={relatedTask.Created}/>
            {` (${document.id})`}
        </span>;
    }

    private findLinksToDocumentsIn(state: any) {
        const links = Object.keys(state)
            .map(key => {
                const value = state[key];

                if (Array.isArray(value)) {
                    return this.tryFindLinksToDocuments(value);
                }

                return this.tryFindLinksToDocuments([value]);
            })
            .reduce((accumulator, current) => accumulator.concat(current), [])
            .filter(value => !!value);

        return uniqBy(links, l => l.id);
    }

    private tryFindLinksToDocuments(values: any[]) {
        return values.map(value => {
            if (typeof value === "string") {
                const url = this.tryFindLinkToDocument(value);
                if (!url) {
                    return null;
                }
                return {id: value, url};
            }

            return null;
        });
    }

    private tryFindLinkToDocument(value: string) {
        value = value.toLocaleLowerCase();

        if (value.startsWith("hostedinstances-")) {
            return routeLinks.instances.instance(value).root;
        }

        if (value.startsWith("reefs-")) {
            return routeLinks.reefs.reef(value).root;
        }

        if (value.startsWith("backgroundtasks-")) {
            return routeLinks.backgroundTasks.task(value).root;
        }

        if (value.startsWith("hubs-")) {
            return routeLinks.hubs.hub(value).root;
        }

        if (value.startsWith("azureregions-")) {
            return routeLinks.azureRegions.azureRegion(value).root;
        }

        return null;
    }

    private getProcessDialog() {
        const task = this.state.task;
        const process = task ? this.state.processes.filter(p => p.Id === task.ProcessId)[0] : null;

        return process == null
            ? <span>Process not found</span>
            : <div className={stepsListStyle}>
                <List<Step>
                    items={process.Steps}
                    rowKey={(step) => step.Id}
                    renderRow={({item: step}) => (
                        <ListItem>
                            <ListItemContent>
                                <div style={{display: "flex", alignItems: "center", gap: 12, minHeight: "2.25rem"}}>
                                    <div style={{flexShrink: 0, width: 80}}>{this.getCurrentStateText(step, task)}</div>
                                    <div style={{flex: 1}}>
                                        <div>{this.getTaskStepText(step, task)}</div>
                                        {step.Description && <div style={{color: themeTokens.color.text.tertiary, fontSize: "0.875rem", marginTop: "4px"}}>{step.Description}</div>}
                                    </div>
                                    <div style={{flexShrink: 0}}>{this.getAction(step, task)}</div>
                                </div>
                            </ListItemContent>
                        </ListItem>
                    )}
                    accessibleName="Task steps"
                />
            </div>;
    }

    private getLockWarning(instanceLock: InstanceTaskLockResource, task: BackgroundTaskResource) {
        return <>{this.showLockWarning(instanceLock, task) && <Callout type="warning" title={"Task queued"}>
            The <InternalLink to={routeLinks.instanceTaskLocks.filtered({ relatedToHostedInstanceIds: [instanceLock.HostedInstanceId]})}>Instance Lock</InternalLink> for <InternalLink to={routeLinks.instances.instance(instanceLock.HostedInstanceId).root}>{instanceLock.HostedInstanceId}</InternalLink> is currently held by task
            <InternalLink to={routeLinks.backgroundTasks.task(instanceLock.LockOwnerId).root}> {instanceLock.LockOwnerId}</InternalLink> ({displayTaskStatus(instanceLock.LockOwnerStatus)}).
            This task will not be executed until the lock is released.
        </Callout>}</>
    }

    private showLockWarning(instanceLock: InstanceTaskLockResource, task: BackgroundTaskResource) {
        return instanceLock &&
            task.EffectiveLockOwnerId !== instanceLock.LockOwnerId &&
            task.Status === BackgroundTaskStatus.Sleeping;
    }

    private toggleSubscription = async () => {
        if (this.state.subscribed) {
            await repository.BackgroundTasks.unsubscribe(this.state.task.Id);
        } else {
            await repository.BackgroundTasks.subscribe(this.state.task.Id);
        }
        return await this.updateSubscriptionState(!this.state.subscribed);
    }

    private manageSubscriptionDialog() {
        return <ManageSubscriptionDialog
            task={this.state.task}
            busy={this.state.busy}
            errors={this.state.errors}
            onSaveClick={this.updateSubscriptionState}/>;
    }

    private updateSubscriptionState = async (subscription: any) => {
        this.setState({subscribed: !!subscription});
        return true;
    };

    private doRefresh: Refresh = () => Promise.resolve();

    private diagnosticFilesChanged = () => {
        return this.doBusyTask(async () => {
            this.setState({diagnosticFilesLoaded: false });
            await this.doRefresh();
            return true;
        });
    };

    private cloudPortalFilesChanged = () => {
        return this.doBusyTask(async () => {
            this.setState({cloudPortalFilesLoaded: false });
            await this.doRefresh();
            return true;
        });
    };
}
